import React, { memo, ReactNode } from 'react';

import { FieldInputProps, useField } from 'react-final-form';
import { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { isEqual } from 'lodash';
import { InitialEditorStateType } from '@lexical/react/LexicalComposer';

import { WysiwygInput as Input } from '@vk-hr-tek/ui/input';

type ValidateType = (
  value: any,
  allValues?: any,
) => (string | undefined) | Promise<string | undefined>;

const handleChange =
  (
    input: FieldInputProps<InitialEditorStateType | undefined, HTMLElement>,
    onChange?: (body: {
      data: SerializedEditorState<SerializedLexicalNode>;
      hasContent: boolean;
    }) => void,
  ) =>
  (body: {
    data: SerializedEditorState<SerializedLexicalNode>;
    hasContent: boolean;
  }) => {
    input.onChange(body.data);

    onChange?.(body);
  };

const handleBlur =
  (input: FieldInputProps<InitialEditorStateType | undefined, HTMLElement>) =>
  () => {
    input.onBlur();
  };

const handleFocus =
  (input: FieldInputProps<InitialEditorStateType | undefined, HTMLElement>) =>
  () => {
    input.onFocus();
  };

interface WysiwygInputProps {
  config?: {
    plugins: {
      selectAllPlugin: { value: boolean; withDivider: boolean };
      formatHeadingPlugin: {
        heading: boolean;
        fontColor: boolean;
        backgroungColor: boolean;
        withDivider: boolean;
      };
      formatTextPlugin: {
        bold: boolean;
        italic: boolean;
        underline: boolean;
        strikethrough: boolean;
        code: boolean;
        superscript: boolean;
        subscript: boolean;
        withDivider: boolean;
      };
      listAndFormatPlugin: {
        align: boolean;
        list: boolean;
        indent: boolean;
        withDivider: boolean;
      };
      linkAndTablePlugin: {
        link: boolean;
        table: boolean;
        reference: boolean;
        codeBlock: boolean;
        quote: boolean;
        symbols: boolean;
        mention: boolean;
        withDivider: boolean;
      };
      mediaPlugin: {
        singeImage: boolean;
        imagesGallery: boolean;
        video: boolean;
        file: boolean;
        withDivider: boolean;
      };
      undoRedoPlugin: boolean;
      trackMediaIdsAndMentionsPlugin: boolean;
    };
    mode: 'default' | 'comments';
  };
  focusMode?: 'auto' | 'manual';
  name: string;
  onChange?: (body: {
    data: SerializedEditorState<SerializedLexicalNode>;
    hasContent: boolean;
  }) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  tooltip?: ReactNode | null;
  debounceTimeout?: number;
  maxCharLimit?: number;
  loadFile: (
    file: File,
    type: 'file' | 'image',
  ) => Promise<{
    name: string;
    id: string;
    type: 'image' | 'file';
  }>;
  getAttachmentUrl: (id: string) => Promise<string>;
  searchMentions?: (params: {
    query: string;
    offset: number;
    limit: number;
  }) => Promise<{
    mentions: {
      id: string;
      type: 'user' | 'unit';
      data:
        | {
            id: string;
            first_name: string;
            second_name: string;
            middle_name: string;
            logo?: string;
          }
        | { id: string; name: string };
    }[];
    total: number;
  }>;
  onUploadError: (message: string) => void;
  validateArray?: ValidateType[];
  getCurrentMediaIds?: (mediaIds: string[]) => void;
  checkEditorContainsMention?: (hasMention: boolean) => void;
  getCurrentMentionsData?: (
    data: {
      id: string;
      type: string;
    }[],
  ) => void;
  handleSendClick?: () => void;
  editable?: boolean;
  hideSubmitButton?: boolean;
  apiUrl: string;
  isSubmitting?: boolean;
  showErrors?: boolean;
  initialValue?: SerializedEditorState<SerializedLexicalNode>;
}

const arePropsEqual = (
  oldProps: Record<string, unknown>,
  newProps: Record<string, unknown>,
) => isEqual(oldProps, newProps);

export const WysiwygInput = memo(
  ({
    config = {
      plugins: {
        selectAllPlugin: { value: true, withDivider: true },
        formatHeadingPlugin: {
          heading: true,
          fontColor: true,
          backgroungColor: true,
          withDivider: true,
        },
        formatTextPlugin: {
          bold: true,
          italic: true,
          underline: true,
          strikethrough: true,
          code: true,
          superscript: true,
          subscript: true,
          withDivider: true,
        },
        listAndFormatPlugin: {
          align: true,
          list: true,
          indent: true,
          withDivider: true,
        },
        linkAndTablePlugin: {
          link: true,
          table: true,
          reference: true,
          codeBlock: true,
          quote: true,
          symbols: true,
          mention: true,
          withDivider: true,
        },
        mediaPlugin: {
          singeImage: true,
          imagesGallery: true,
          video: true,
          file: true,
          withDivider: true,
        },
        undoRedoPlugin: true,
        trackMediaIdsAndMentionsPlugin: true,
      },
      mode: 'default',
    },
    name,
    onChange,
    placeholder,
    label,
    required = false,
    tooltip = null,
    loadFile,
    getAttachmentUrl,
    searchMentions,
    onUploadError,
    validateArray,
    getCurrentMediaIds,
    getCurrentMentionsData,
    checkEditorContainsMention,
    handleSendClick,
    focusMode = 'manual',
    debounceTimeout,
    maxCharLimit,
    editable,
    hideSubmitButton,
    apiUrl,
    isSubmitting = false,
    showErrors = true,
    initialValue,
    ...rest
  }: WysiwygInputProps) => {
    const composeValidators =
      (validators: ValidateType[]) =>
      (value: Record<string, any> | any, allValues: Record<string, any>) => {
        const result = validators.reduce(
          (error: any, validator?: ValidateType) =>
            error || validator?.(value, allValues),
          undefined,
        );
        return result;
      };

    const { input, meta } = useField(name, {
      validate: composeValidators(validateArray || []),
      subscription: { value: true, touched: true, error: true },
      ...rest,
    });

    return (
      <Input
        {...input}
        initialValue={initialValue}
        apiUrl={apiUrl}
        config={config}
        onChange={handleChange(input, onChange)}
        onBlur={handleBlur(input)}
        onFocus={handleFocus(input)}
        placeholder={placeholder}
        label={label}
        required={required}
        tooltip={tooltip}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
        loadFile={loadFile}
        getAttachmentUrl={getAttachmentUrl}
        searchMentions={searchMentions}
        onUploadError={onUploadError}
        getCurrentMediaIds={getCurrentMediaIds}
        getCurrentMentionsData={getCurrentMentionsData}
        checkEditorContainsMention={checkEditorContainsMention}
        handleSendClick={handleSendClick}
        focusMode={focusMode}
        debounceTimeout={debounceTimeout}
        maxCharLimit={maxCharLimit}
        editable={editable}
        hideSubmitButton={hideSubmitButton}
        isSubmitting={isSubmitting}
        showErrors={showErrors}
      />
    );
  },
  arePropsEqual,
);
